import React, { useState } from 'react';
import axios from 'axios';
import API from '../config';

const OTPVerify = ({ email, onDone, onResend, T }) => {
  const [otp, setOtp]         = useState(['', '', '', '', '', '']);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [cooldown, setCooldown]   = useState(0);
  const inputs = React.useRef([]);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    setError('');
    if (val && i < 5) inputs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) { setOtp(paste.split('')); inputs.current[5]?.focus(); }
    e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) { setError('Enter the full 6-digit code'); return; }
    setLoading(true);
    try {
      await onDone(code);
    } catch (err) {
      setError(err.message || 'Verification failed. Try again.');
      setOtp(['', '', '', '', '', '']);
      inputs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg('');
    setError('');
    const result = await onResend();
    if (result?.success) {
      setResendMsg('New code sent!');
      setCooldown(60);
      setOtp(['', '', '', '', '', '']);
      inputs.current[0]?.focus();
    } else {
      setError(result?.error || 'Failed to resend. Try again.');
    }
    setResending(false);
  };

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => a + '*'.repeat(Math.min(b.length, 4)) + c)
    : 'your email';

  return (
    <div style={{ width: '100%', maxWidth: 420 }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${T.accent}1a`, border: `1px solid ${T.accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 24 }}>✉️</div>
        <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 8 }}>Verify your email</h1>
        <p style={{ color: T.textMuted, fontSize: 14, lineHeight: 1.6 }}>
          We sent a 6-digit code to<br />
          <span style={{ color: T.text, fontWeight: 600, fontFamily: "'DM Mono', monospace" }}>{maskedEmail}</span>
        </p>
      </div>

      {error     && <div style={{ background: `${T.no}14`, border: `1px solid ${T.no}33`, color: T.no, padding: '10px 14px', borderRadius: 7, marginBottom: 20, fontSize: 13, textAlign: 'center' }}>{error}</div>}
      {resendMsg && <div style={{ background: `${T.yes}14`, border: `1px solid ${T.yes}33`, color: T.yes, padding: '10px 14px', borderRadius: 7, marginBottom: 20, fontSize: 13, textAlign: 'center' }}>{resendMsg}</div>}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 28 }}>
          {otp.map((digit, i) => (
            <input key={i} ref={el => inputs.current[i] = el}
              type="text" inputMode="numeric" maxLength={1} value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              onPaste={handlePaste}
              autoFocus={i === 0}
              style={{ width: 52, height: 58, textAlign: 'center', background: T.surface, border: `1px solid ${digit ? T.accent : T.border}`, borderRadius: 10, color: T.text, fontSize: 24, fontWeight: 700, fontFamily: "'DM Mono', monospace", outline: 'none', transition: 'border-color 0.15s' }} />
          ))}
        </div>
        <button type="submit" disabled={loading || otp.join('').length !== 6}
          style={{ width: '100%', background: otp.join('').length !== 6 ? T.surfaceHi : T.accent, color: otp.join('').length !== 6 ? T.textMuted : '#fff', border: 'none', borderRadius: 8, padding: 13, fontWeight: 700, cursor: otp.join('').length !== 6 ? 'not-allowed' : 'pointer', fontSize: 14, marginBottom: 16, transition: 'all 0.15s' }}>
          {loading ? 'Verifying...' : 'Verify email →'}
        </button>
      </form>

      <div style={{ textAlign: 'center' }}>
        <p style={{ color: T.textMuted, fontSize: 13 }}>
          Didn't receive the code?{' '}
          {cooldown > 0
            ? <span style={{ color: T.textDim, fontFamily: "'DM Mono', monospace" }}>Resend in {cooldown}s</span>
            : <span onClick={!resending ? handleResend : undefined} style={{ color: resending ? T.textDim : T.accent, cursor: resending ? 'default' : 'pointer', fontWeight: 600 }}>
                {resending ? 'Sending...' : 'Resend code'}
              </span>
          }
        </p>
      </div>
    </div>
  );
};

export default OTPVerify;